// IndexedDB offline store for Coinka PWA
// Database: coinka_offline, version 1
// Stores: pending_operations, cached_transactions, cached_categories, cached_accounts, sync_meta

class OfflineStore {
  constructor() {
    this.dbName = "coinka_offline"
    this.dbVersion = 1
    this.db = null
  }

  async open() {
    if (this.db) return this.db
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion)
      request.onupgradeneeded = (e) => {
        const db = e.target.result
        if (!db.objectStoreNames.contains("pending_operations")) {
          db.createObjectStore("pending_operations", { keyPath: "client_id" })
        }
        if (!db.objectStoreNames.contains("cached_transactions")) {
          db.createObjectStore("cached_transactions", { keyPath: "id" })
        }
        if (!db.objectStoreNames.contains("cached_categories")) {
          db.createObjectStore("cached_categories", { keyPath: "id" })
        }
        if (!db.objectStoreNames.contains("cached_accounts")) {
          db.createObjectStore("cached_accounts", { keyPath: "id" })
        }
        if (!db.objectStoreNames.contains("sync_meta")) {
          db.createObjectStore("sync_meta", { keyPath: "key" })
        }
      }
      request.onsuccess = (e) => {
        this.db = e.target.result
        resolve(this.db)
      }
      request.onerror = (e) => reject(e.target.error)
    })
  }

  // Generate UUID for client_id
  generateId() {
    return crypto.randomUUID()
  }

  // -- Pending Operations Queue --

  async addOperation(type, data) {
    const db = await this.open()
    const now = new Date().toISOString()
    const op = {
      client_id: this.generateId(),
      type,
      data: { ...data, offline_created_at: now },
      created_at: now
    }
    return new Promise((resolve, reject) => {
      const tx = db.transaction("pending_operations", "readwrite")
      tx.objectStore("pending_operations").add(op)
      tx.oncomplete = () => resolve(op)
      tx.onerror = (e) => reject(e.target.error)
    })
  }

  async getPendingOperations() {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction("pending_operations", "readonly")
      const request = tx.objectStore("pending_operations").getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = (e) => reject(e.target.error)
    })
  }

  async removePendingOperations(clientIds) {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction("pending_operations", "readwrite")
      const store = tx.objectStore("pending_operations")
      clientIds.forEach(id => store.delete(id))
      tx.oncomplete = () => resolve()
      tx.onerror = (e) => reject(e.target.error)
    })
  }

  async getPendingCount() {
    const ops = await this.getPendingOperations()
    return ops.length
  }

  // -- Cache Management --

  async cacheData(storeName, items) {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, "readwrite")
      const store = tx.objectStore(storeName)
      items.forEach(item => store.put(item))
      tx.oncomplete = () => resolve()
      tx.onerror = (e) => reject(e.target.error)
    })
  }

  async getCachedData(storeName) {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, "readonly")
      const request = tx.objectStore(storeName).getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = (e) => reject(e.target.error)
    })
  }

  async clearStore(storeName) {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, "readwrite")
      tx.objectStore(storeName).clear()
      tx.oncomplete = () => resolve()
      tx.onerror = (e) => reject(e.target.error)
    })
  }

  // -- Sync Meta --

  async getLastSyncTime() {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction("sync_meta", "readonly")
      const request = tx.objectStore("sync_meta").get("last_sync")
      request.onsuccess = () => resolve(request.result?.value || null)
      request.onerror = (e) => reject(e.target.error)
    })
  }

  async setLastSyncTime(time) {
    const db = await this.open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction("sync_meta", "readwrite")
      tx.objectStore("sync_meta").put({ key: "last_sync", value: time })
      tx.oncomplete = () => resolve()
      tx.onerror = (e) => reject(e.target.error)
    })
  }
}

export const offlineStore = new OfflineStore()
